import BaseImage from "@/app/(site)/_components/Atoms/BaseImage"
import HeadingElement from "@/app/(site)/_components/GCMolecules/HeadingElement"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { Badges } from "@/app/(site)/_components/Page/Product/ProductDetail/Badges"
import RelatedProductDetails from "@/app/(site)/_components/Page/Product/ProductDetail/RelatedProducts/RelatedProductDetials"
import { getSalePercentage } from "@/app/(site)/_utils/getSalePercentage"
import { type CentraLanguageCode } from "@/data/supported-languages"
import { type RelatedProduct } from "@/lib/content-service/types/ContentServiceProduct"
import { FOCUS_VISIBLE_INSET_CLASS } from "@/src/utils/a11y/focusVisible"
import { resolveSanityInjectBadges } from "@/src/utils/badges/resolveSanityInjectBadges"
import { clsx } from "@/src/utils/clsx"
import placeholder from "@assets/images/placeholder.png"
import { useState } from "react"

interface RelatedProductProps {
    products: RelatedProduct[]
    langCode: CentraLanguageCode
    locale: string
}

interface RelatedProductCardProps {
    product: RelatedProduct
    langCode: CentraLanguageCode
    locale: string
}

function RelatedProductCard({ product, langCode, locale }: RelatedProductCardProps) {
    const [isHovered, setIsHovered] = useState(false)

    const hasMedia = Array.isArray(product.media) && product.media.length > 0
    const imageSrc = hasMedia ? product.media[0]?.url : placeholder.src
    const hasHoverImage = hasMedia && product.media?.[1]?.url

    const sanityBadges = resolveSanityInjectBadges(
        product.sanity_inject_badge_json,
        langCode,
        locale
    )

    return (
        <DynamicLink
            href={`/product/${product.uri}`}
            className={clsx(
                "relative flex h-full w-[180px] min-w-[180px] flex-col gap-y-8 temp-md:w-full temp-md:min-w-0",
                FOCUS_VISIBLE_INSET_CLASS
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            prefetch={false}
        >
            {(sanityBadges.length > 0 ||
                (Array.isArray(product.combinedBadges) &&
                    product.combinedBadges.length > 0)) && (
                <div className="absolute left-8 right-8 top-8 z-10 flex flex-wrap">
                    <Badges
                        combinedBadges={product.combinedBadges ?? []}
                        discountPercent={getSalePercentage(product)}
                        resolvedSanityBadges={sanityBadges}
                    />
                </div>
            )}
            <div className="relative aspect-[0.7] w-full bg-col-bg-calm lg:min-w-0">
                {!isHovered && (
                    <BaseImage
                        src={imageSrc}
                        alt=""
                        loading="lazy"
                        fill
                        unoptimized
                        className="object-contain"
                    />
                )}
                {hasHoverImage && isHovered && (
                    <BaseImage
                        src={product.media[1]?.url}
                        alt=""
                        loading="lazy"
                        fill
                        unoptimized
                        className="object-contain"
                    />
                )}
            </div>
            <RelatedProductDetails
                name={product.name}
                priceBeforeDiscount={product.priceBeforeDiscount}
                showAsOnSale={product.showAsOnSale}
                price={product.price}
                uri={product.uri}
            />
        </DynamicLink>
    )
}

export default function RelatedProducts({
    products,
    langCode,
    locale
}: RelatedProductProps) {
    return (
        <div className="col-start-1 flex flex-col gap-12 lg:col-span-8 lg:col-start-5">
            <HeadingElement
                isAboveTheFold={false}
                text="Combine with"
                type="h3"
                styleKey="Trim_Mono_POC_Text_14_130_-5_400_uppercase"
                className="px-12 temp-md:px-0"
            />
            <div className="no-scrollbar relative flex gap-x-12 overflow-x-auto px-12 temp-md:grid temp-md:grid-cols-4 temp-md:px-0 lg:gap-x-16">
                {products.slice(0, 3).map(product => (
                    <RelatedProductCard
                        key={product.sku}
                        product={product}
                        langCode={langCode}
                        locale={locale}
                    />
                ))}
            </div>
        </div>
    )
}
